import { select } from 'd3-selection';
import { line } from 'd3-shape';
import { useEffect, useRef } from 'react';
import { convertBeatTypeNumberToColor, convertBeatTypeNumberToText } from '@/shared/ui';
import {
  calculateConfig,
  calculatePixelSamples,
  calculateSamples,
  CONFIG_ECG,
} from '@/entities/ecg';
import type { ThumbnailBeatProps } from './thumbnailBeat';

/**
 * Renders a small ECG thumbnail for one beat into an SVG element, showing the waveform, a centre marker and per-beat type labels. Derives the display config from gain, speed, size and high-pass settings, resamples the selected channels to pixel space, and on every data or setting change clears the SVG and redraws the waveform paths, the dashed centre line and the coloured beat-type text.
 *
 * @param props - Thumbnail inputs: ECG samples, beat markers, per-beat status codes, channels, gain, speed, width, height and high-pass cutoff.
 * @returns An object carrying canvasRef, the ref to attach to the target SVG element.
 */
const useThumbnailBeat = ({
  ecgData,
  beatMarkers,
  hesBeatStatus,
  channels = [2],
  gain,
  speed,
  width,
  height,
  highPass,
}: Pick<
  ThumbnailBeatProps,
  | 'ecgData'
  | 'beatMarkers'
  | 'hesBeatStatus'
  | 'channels'
  | 'gain'
  | 'speed'
  | 'width'
  | 'height'
  | 'highPass'
>) => {
  const canvasRef = useRef<SVGSVGElement>(null);
  const drawStrip = ({
    ecgData,
    beatMarkers,
    hesBeatStatus,
    channels = [],
  }: {
    ecgData: number[][];
    beatMarkers: number[];
    hesBeatStatus: number[];
    channels?: number[];
  }) => {
    if (channels.length === 0) {
      return;
    }
    const countChannels = ecgData.length;
    const formatChannels = channels.map((x) => x - 1);
    const drawEcgData: number[][] = [];
    if (countChannels > 1) {
      formatChannels.forEach((x) => {
        drawEcgData.push(ecgData[x]);
      });
    } else {
      drawEcgData.push(ecgData[0]);
    }

    // calculateConfig's JSDoc param type (BeatHR/helper, not yet converted) also
    // declares a pHFF field this call site never passed.
    const config = calculateConfig({
      pGain: gain,
      pSpeed: speed,
      width,
      height,
      adcGain: CONFIG_ECG.gain,
      ecgData: drawEcgData,
      samplingRate: CONFIG_ECG.samplingFrequency,
      pLFF: highPass,
    } as any);
    const samples = calculateSamples(drawEcgData, config);
    const pixelSamples = calculatePixelSamples(samples, config);
    const { indexToPix } = config;
    const pixelBeats = beatMarkers.map((beatMarker) => Math.floor(beatMarker * indexToPix));

    if (canvasRef.current) {
      const svg = select(canvasRef.current);
      svg.selectAll('*').remove();
      const lineFunc = line<{ x: number; y: number }>()
        .x((d) => d.x)
        .y((d) => d.y);
      pixelSamples.forEach((displayedSamples: any) => {
        svg
          .append('path')
          .attr('d', lineFunc(displayedSamples))
          .attr('stroke', '#1D2F47')
          .attr('stroke-width', '1px')
          .attr('fill', 'none');
      });
      svg
        .append('path')
        .attr(
          'd',
          lineFunc([
            { x: width / 2, y: 0 },
            { x: width / 2, y: height },
          ]),
        )
        .style('stroke-dasharray', '2')
        .attr('stroke', '#0B68E5')
        .attr('fill', 'none');

      pixelBeats.forEach((pixelBeat, index) => {
        svg
          .append('text')
          .attr('x', pixelBeat)
          .attr('y', 10)
          .attr('text-anchor', 'middle')
          .style('font-size', '10px')
          .style('font-weight', '600')
          .style('fill', convertBeatTypeNumberToColor(hesBeatStatus[index]))
          .text(
            hesBeatStatus[index] !== 0 ? convertBeatTypeNumberToText(hesBeatStatus[index]) : '',
          );
      });
    }
  };

  useEffect(() => {
    if (ecgData?.length > 0) {
      drawStrip({
        ecgData,
        beatMarkers,
        hesBeatStatus,
        channels,
      });
    }
  }, [
    ecgData,
    JSON.stringify(channels),
    gain,
    highPass,
    width,
    height,
    beatMarkers,
    hesBeatStatus,
  ]);

  return { canvasRef };
};

export default useThumbnailBeat;
